import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { useLang, localName, localDesc } from "../i18n";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Eyebrow, Reveal, GoldButton, OutlineButton } from "../components/motion";
import { VerifiedBadge } from "../components/ChefCard";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function Booking() {
  const { t, lang } = useLang();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [params] = useSearchParams();
  const chefSlug = params.get("chef");

  const [step, setStep] = useState(1);
  const [dishId, setDishId] = useState(params.get("dish") || "");
  const [details, setDetails] = useState({ guests: 4, date: "", time: "19:30", address: "", city: "", occasion: "", special_requests: "" });
  const [booking, setBooking] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedNoStripe, setSavedNoStripe] = useState(false);

  const { data: chef } = useQuery({
    queryKey: ["chef", chefSlug],
    queryFn: () => api.get(`/chefs/slug/${chefSlug}`).then((r) => r.data),
    enabled: !!chefSlug,
  });
  const { data: payCfg } = useQuery({ queryKey: ["paycfg"], queryFn: () => api.get("/payments/config").then((r) => r.data) });

  const selectedDish = useMemo(() => chef?.dishes?.find((d) => d.id === dishId), [chef, dishId]);

  if (ready && !user) {
    return (
      <div className="pt-[72px] min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="font-serif text-2xl text-ivory mb-6">{t("booking.loginFirst")}</p>
          <Link to={`/${lang}/login?next=${encodeURIComponent(pathname.replace(`/${lang}`, "") + search)}`}>
            <GoldButton data-testid="booking-login-btn">{t("nav.login")}</GoldButton>
          </Link>
        </div>
      </div>
    );
  }
  if (!chefSlug) {
    return (
      <div className="pt-[72px] min-h-[70vh] flex items-center justify-center px-4 text-center">
        <div>
          <p className="text-neutral-400 mb-6">{t("booking.selectDish")}</p>
          <Link to={`/${lang}/chefs`}><GoldButton>{t("nav.findChef")}</GoldButton></Link>
        </div>
      </div>
    );
  }

  const createBooking = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bookings", {
        dish_id: dishId,
        guests: Number(details.guests),
        date: details.date,
        time: details.time,
        address: details.address,
        city: details.city,
        occasion: details.occasion || undefined,
        special_requests: details.special_requests || undefined,
        customizations: [],
      });
      const sel = await api.post(`/bookings/${r.data.id}/select-chef`, { chef_id: chef.id });
      setBooking(sel.data);
      setStep(3);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const applyPromo = async () => {
    if (!promo) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/apply-promo`, { code: promo });
      setBooking(r.data.booking || r.data);
      setPromoApplied(true);
      toast.success(t("booking.promoApplied"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: `${window.location.origin}/${lang}`,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (err) {
      if (err?.response?.status === 503 || !payCfg?.stripe_enabled) setSavedNoStripe(true);
      else toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (e) => setDetails({ ...details, [k]: e.target.value });
  const steps = [t("booking.step1"), t("booking.step2"), t("booking.step3")];

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("booking.title")} — Chef à Domicile`} description={t("seo.homeDesc")} />
      <div className="max-w-[900px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <Reveal>
          <Eyebrow>Chef à Domicile</Eyebrow>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory mb-10">{t("booking.title")}</h1>
        </Reveal>

        <div className="flex items-center gap-0 mb-14" data-testid="booking-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 flex items-center justify-center border text-sm font-serif ${step > i ? "bg-gold text-black border-gold" : "border-white/20 text-neutral-500"}`}>
                  {step > i + 1 ? <Check size={14} /> : i + 1}
                </span>
                <span className={`text-xs uppercase tracking-widest hidden sm:block ${step === i + 1 ? "text-gold" : "text-neutral-500"}`}>{s}</span>
              </div>
              {i < 2 && <span className="flex-1 h-px bg-white/10 mx-4" />}
            </React.Fragment>
          ))}
        </div>

        {chef && (
          <div className="flex items-center gap-4 bg-coal border border-white/8 p-5 mb-10" data-testid="booking-chef-summary">
            <img src={chef.photo} alt={chef.name} className="w-16 h-16 object-cover" />
            <div>
              <p className="font-serif text-lg text-ivory">{chef.name}</p>
              <p className="text-xs text-neutral-500">{chef.city}</p>
            </div>
            <div className="ms-auto"><VerifiedBadge small /></div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="booking-step-1">
            <h2 className="font-serif text-xl text-ivory mb-6">{t("booking.selectDish")}</h2>
            <div className="grid sm:grid-cols-2 gap-5 mb-10">
              {chef?.dishes?.map((d) => (
                <button key={d.id} data-testid={`booking-dish-${d.id}`} onClick={() => setDishId(d.id)}
                  className={`text-start border transition-colors ${dishId === d.id ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`}>
                  <img src={d.image} alt={d.name} loading="lazy" className="w-full aspect-[16/8] object-cover" />
                  <div className="p-5">
                    <div className="flex justify-between gap-3">
                      <p className="font-serif text-lg text-ivory">{d.name}</p>
                      <p className="text-gold font-serif">€{d.price_per_guest}</p>
                    </div>
                    <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed">{localDesc(d, lang)}</p>
                  </div>
                </button>
              ))}
            </div>
            <GoldButton disabled={!dishId} onClick={() => setStep(2)} data-testid="booking-step1-next">{t("common.next")}</GoldButton>
          </div>
        )}

        {step === 2 && (
          <div data-testid="booking-step-2" className="bg-coal border border-white/8 p-8 md:p-10">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="lux-label">{t("search.guests")} *</label>
                <input data-testid="booking-guests-input" type="number" min="1" max="50" required className="lux-input" value={details.guests} onChange={upd("guests")} /></div>
              <div><label className="lux-label">{t("booking.date")} *</label>
                <input data-testid="booking-date-input" type="date" required className="lux-input" value={details.date} onChange={upd("date")} /></div>
              <div><label className="lux-label">{t("booking.time")} *</label>
                <input data-testid="booking-time-input" type="time" required className="lux-input" value={details.time} onChange={upd("time")} /></div>
              <div><label className="lux-label">{t("booking.city")} *</label>
                <input data-testid="booking-city-input" required className="lux-input" value={details.city} onChange={upd("city")} /></div>
              <div className="sm:col-span-2"><label className="lux-label">{t("booking.address")} *</label>
                <input data-testid="booking-address-input" required className="lux-input" value={details.address} onChange={upd("address")} /></div>
              <div><label className="lux-label">{t("booking.occasion")} ({t("common.optional")})</label>
                <input data-testid="booking-occasion-input" className="lux-input" value={details.occasion} onChange={upd("occasion")} /></div>
              <div><label className="lux-label">{t("booking.requests")} ({t("common.optional")})</label>
                <input data-testid="booking-requests-input" className="lux-input" value={details.special_requests} onChange={upd("special_requests")} /></div>
            </div>
            <div className="flex gap-4 mt-8">
              <OutlineButton onClick={() => setStep(1)} data-testid="booking-step2-back">{t("common.back")}</OutlineButton>
              <GoldButton disabled={busy || !details.date || !details.address || !details.city || !details.guests} onClick={createBooking} data-testid="booking-step2-next">
                {busy ? t("common.loading") : t("booking.create")}
              </GoldButton>
            </div>
          </div>
        )}

        {step === 3 && booking && (
          <div data-testid="booking-step-3" className="bg-coal border border-white/8 p-8 md:p-10">
            {savedNoStripe ? (
              <div className="text-center py-8" data-testid="booking-stripe-off">
                <p className="font-serif text-2xl text-gold mb-4">✓</p>
                <p className="text-ivory leading-relaxed max-w-md mx-auto mb-8">{t("booking.stripeOff")}</p>
                <Link to={`/${lang}/account`}><GoldButton data-testid="booking-go-account">{t("pay.backAccount")}</GoldButton></Link>
              </div>
            ) : (
              <>
                <div className="space-y-4 border-b border-white/10 pb-6 mb-6">
                  <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("booking.chef")}</span><span className="text-ivory">{booking.chef_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("booking.step1")}</span><span className="text-ivory">{booking.dish_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("search.guests")}</span><span className="text-ivory">{booking.guests}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("booking.date")}</span><span className="text-ivory">{booking.date} · {booking.time}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("booking.pricePerGuest")}</span><span className="text-ivory">€{booking.price_per_guest}</span></div>
                  {booking.discount > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-neutral-400">{t("booking.discount")}</span><span className="text-gold">−€{booking.discount}</span></div>
                  )}
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-neutral-300 uppercase tracking-widest text-xs">{t("common.total")}</span>
                    <span className="font-serif text-3xl text-gold" data-testid="booking-total">€{booking.total}</span>
                  </div>
                </div>
                <div className="flex gap-3 mb-8">
                  <input data-testid="booking-promo-input" className="lux-input flex-1" placeholder={t("booking.promo")} value={promo} onChange={(e) => setPromo(e.target.value)} disabled={promoApplied} />
                  <OutlineButton onClick={applyPromo} disabled={busy || promoApplied || !promo} data-testid="booking-promo-apply">
                    {promoApplied ? "✓" : t("booking.promoApply")}
                  </OutlineButton>
                </div>
                <GoldButton onClick={pay} disabled={busy} data-testid="booking-pay-btn" className="w-full">
                  {busy ? t("common.loading") : t("booking.pay")}
                </GoldButton>
                <p className="text-xs text-neutral-500 mt-4 leading-relaxed">{t("booking.payNote")}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
